<template>
  <div class="study-set-container">
    <el-row>
      <el-col :span="isGeneralAdmin || hasFilter ? 12 : 24">
        <organization-breadcrumb :items="[$t('study_sets.curriculum')]" />
      </el-col>
      <el-col v-if="isGeneralAdmin || hasFilter" :span="12">
        <admin-organization-switcher />
      </el-col>
    </el-row>
    <div class="study-set-tab-body">
      <curriculum-menu :active-entry="`/organization/${organizationId}/studysets`" />
      <template>
        <div class="no-study-set" v-if="!loading && !showTable">
          <curriculum-image />
          <h2 style="font-weight: 600; font-size: 32px; text-align: center">
            {{ $t("study_sets.no_study_sets_yet") }}
          </h2>
          <p style="font-size: 16px">
            {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.no_study_sets_desc`)) }}
          </p>
          <div class="create-new-study-set">
            <router-link
              :to="'/organization/' + organizationId + '/curriculum/studysets/new'"
              v-if="isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher"
              tag="button"
            >
              {{ $t("study_sets.create_study_set") }}
            </router-link>
          </div>
        </div>
        <div v-else class="elsa-table-layout-header">
          <div class="header-end">
            <div class="more-action-text">
              <label class="mr-px-16">
                {{ $t("homework.assignment_lib.table_selected_label", [selectedStudySets.length, studySetsCount]) }}
              </label>
              <a @click="selectAll(paginateStudySets.results)">
                {{ selectText }}
              </a>
            </div>
            <div style="padding-top: 14px" v-if="selectedStudySets && selectedStudySets.length > 0">
              <div class="assign-to-group">
                <el-button @click="changeActions('assign')">
                  <span>
                    {{ $t($i18nLabelsHelper(currentOrganizationType, "study_sets.assign_to_group")) }}
                  </span>
                </el-button>
              </div>
            </div>
          </div>
          <div class="header-end">
            <div>
              <el-form class="search-form">
                <el-tooltip
                  :visible-arrow="false"
                  :content="$t('study_sets.table.search_hint')"
                  placement="bottom-start"
                >
                  <elsa-search
                    v-model="queryString"
                    @search="searchStudySet"
                    :placeholder="$t('study_sets.table.search_study_set_name')"
                  />
                </el-tooltip>
              </el-form>
            </div>
            <div class="create-new-study-set">
              <router-link
                :to="'/organization/' + organizationId + '/curriculum/studysets/new'"
                v-if="isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher"
                tag="button"
              >
                <span>
                  {{ $t("study_sets.create_study_set") }}
                </span>
              </router-link>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="remove-btn-div" v-if="selectedStudySets && selectedStudySets.length > 0">
      <el-button slot="reference" @click="changeActions('remove')" class="remove-btn">
        <font-awesome-icon :icon="['fas', 'trash-alt']" style="color: #eb5757; font-size: 14px; line-height: 14px" />
      </el-button>
    </div>
    <base-table-loading v-if="loading" :data="skeletonData" />
    <template v-else>
      <table-layout
        v-if="showTable"
        :showTitle="false"
        :pager-counter="pageCounter"
        :pageSize="numberElementsPerPage"
        :total="studySetsCount"
        :currentChange="getStudySetPage"
        :current-page.sync="currentPage"
        :query-placeholder="$t('common.search')"
        :title="$t('study_sets.curriculum')"
        v-loading="loading"
      >
        <div class="table-wrapper">
          <el-table
            ref="multipleTable"
            :data="studySets"
            :default-sort="{ prop: 'updated_at', order: 'descending' }"
            :empty-text="$t('common.no_values')"
            @select="handleSelectRow"
            @sort-change="sortChange"
            @select-all="handleSelectRow"
            @filter-change="handleFilterChange"
          >
            <el-table-column type="selection" width="40"> </el-table-column>
            <el-table-column
              sortable="custom"
              prop="name"
              :label="$t('study_sets.table.study_set_name')"
              :sort-orders="['descending', 'ascending']"
              min-width="150"
            >
              <template slot-scope="scope">
                <router-link :to="'/organization/' + organizationId + '/curriculum/studysets/' + scope.row.id">
                  <span style="font-size: 16px">{{ scope.row.name }}</span>
                </router-link>
              </template>
            </el-table-column>
            <el-table-column width="115" align="center" :label="$t('study_sets.table.skills')">
              <template slot-scope="scope">
                <div class="icon-wrap">
                  <span class="icon" v-for="skill in getSkillsSupport(scope.row)" :key="skill">
                    <i :class="`elsa-skill-` + skill" />
                  </span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              dataType="date"
              sort-by="updated_at"
              prop="updated_at"
              sortable="custom"
              :sort-orders="['descending', 'ascending']"
              :label="$t('study_sets.table.last_updated')"
              align="center"
              width="150"
            >
              <template slot-scope="scope">
                {{ formatDate(scope.row.updated_at) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t($i18nLabelsHelper(currentOrganizationType, `study_sets.recent_assigned_group`))"
              min-width="150"
            >
              <template slot-scope="scope">
                {{ getRecentAssignGroupName(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column
              :filtered-value="filters.category"
              :filters="categoryFilterOptions"
              :label="$t('study_sets.table.category')"
              :label-class-name="isColumnFiltered('category')"
              column-key="category"
              filter-placement="bottom-end"
              prop="category"
              width="120"
            >
              <template slot-scope="scope">
                {{ getCategoryLabel(scope.row.category) }}
              </template>
            </el-table-column>
            <el-table-column prop="phrases_count" :label="$t('study_sets.table.phrases')" align="center">
              <template slot-scope="scope">
                {{ scope.row.phrases_count }}
              </template>
            </el-table-column>
            <el-table-column
              :filtered-value="filters.created_by"
              :filters="createdByFilterOptions"
              :label="$t('study_sets.table.created_by')"
              :label-class-name="isColumnFiltered('created_by')"
              column-key="created_by"
              filter-placement="bottom-end"
              prop="owner_name"
              width="140"
            >
            </el-table-column>
            <el-table-column
              :filtered-value="filters.shareable"
              :filters="shareableFilterOptions"
              :label="$t('study_sets.table.shareable_to')"
              :label-class-name="isColumnFiltered('shareable')"
              column-key="shareable"
              filter-placement="bottom-end"
              prop="shareable"
              width="140"
            >
              <template slot-scope="scope">
                {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.${scope.row.shareable}`)) }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="this.isOrganizationAdmin || isOrganizationAssociateAdmin || isOrganizationTeacher"
              align="right"
              width="220"
            >
              <template slot-scope="scope">
                <div class="flex" style="justify-content: flex-end">
                  <span class="assign-to-class" @click="handleAssignToClassStudySet(scope.row)">
                    <chip>
                      {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.assign_to_group`)) }}
                    </chip>
                  </span>
                  <el-popover
                    placement="bottom-end"
                    trigger="click"
                    :width="130"
                    :visible-arrow="false"
                    style="z-index: 1903"
                  >
                    <div class="actions-list">
                      <span @click="handleDuplicateStudySet(scope.row.id)">
                        <span style="width: 24px; height: 24px; padding-right: 8px">
                          <font-awesome-icon icon="copy" />
                        </span>
                        {{ $t("study_sets.table.duplicate") }}
                      </span>
                      <span
                        @click="handleEditStudySet(scope.row.id)"
                        :class="{ 'btn-edit-disabled': canAccessStudySet(scope.row.id, 'view') }"
                        v-if="!isOrganizationK12"
                      >
                        <span style="width: 24px; height: 24px; padding-right: 8px">
                          <font-awesome-icon icon="pen" />
                        </span>
                        {{ $t("study_sets.table.edit") }}
                      </span>
                      <span v-show="false" @click="handleDeleteStudySet(scope.row.id)" class="remove-study-set">
                        <span style="width: 24px; height: 24px; padding-right: 8px">
                          <font-awesome-icon
                            :icon="['fas', 'trash-alt']"
                            style="color: #eb5757; font-size: 14px; line-height: 14px;"
                          />
                        </span>
                        {{ $t("study_sets.table.remove") }}
                      </span>
                    </div>
                    <el-tooltip :visible-arrow="false" class="item" content="Options" placement="top" slot="reference">
                      <el-button style="padding: 13px 16px; border-radius: 8px">
                        <i class="el-icon-more"></i>
                      </el-button>
                    </el-tooltip>
                  </el-popover>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </table-layout>
    </template>
    <add-assignment-wrapper ref="addAssignment" v-on:reload="handleReload" />
    <confirm-delete-study-set-modal
      :dialogVisible="modals.showConfirmDelete"
      append-to-body
      v-on:closed="modals.showConfirmDelete = false"
      v-on:delete="handleConfirmDeleteStudySet"
    >
      <template v-slot:body>
        <h4 id="leave-modal-title">
          {{ $t($i18nLabelsHelper(currentOrganizationType, `study_sets.modals_delete_title`)) }}
        </h4>
      </template>
    </confirm-delete-study-set-modal>
  </div>
</template>

<script>
import curriculumImage from "@/assets/images/inline.curriculum-image.svg";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import { format } from "date-fns";
import { mapGetters, mapActions } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import TableLayout from "@/components/studysets/TableLayout";
import { always, pathOr } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER } from "@/constants/generic";
import Chip from "@/components/common/Chip";
import AddAssignmentWrapper from "@/components/modals/studysets/AddAssignmentWrapper";
import ElsaSearch from "@/components/studysets/Search";
import { DefaultShareableOptions, DefaultCategoryOptions } from "@/constants/studySets";
import { getOptionsLabels } from "@/utils/elementUi";
import ConfirmDeleteStudySetModal from "@/components/modals/bases/ConfirmDelete";
import CurriculumMenu from "@/components/studysets/CurriculumMenu";
import { DATE_FORMAT } from "@/constants/generic";
import AdminOrganizationSwitcher from "@/components/dashboard/AdminOrganizationSwitcher";
import BaseTableLoading from "@/components/common/BaseTableLoading.vue";
import { STUDY_SET_SKILLS } from "@/constants/studySets";

export default {
  components: {
    BaseTableLoading,
    curriculumImage,
    OrganizationBreadcrumb,
    TableLayout,
    Chip,
    ElsaSearch,
    AddAssignmentWrapper,
    ConfirmDeleteStudySetModal,
    CurriculumMenu,
    AdminOrganizationSwitcher,
  },
  props: {
    dateFormat: {
      type: String,
      default: DATE_FORMAT,
    },
  },
  data() {
    return {
      modals: {
        showConfirmDelete: false,
      },
      deleteStudySetIds: [],
      selectedStudySetToAssign: null,
      activeEntry: "1",
      currentPage: 1,
      queryString: null,
      ordering: "-updated_at",
      loading: true,
      selectedStudySets: [],
      selectedStudySetsCount: 0,
      paginateStudySets: {
        results: [],
        count: 0,
      },
      selectText: this.$t("common.select_all"),
      categoryFilterOptions: [],
      checkedStudySets: {
        view: [],
        edit: [],
      },
      skeletonData: [
        {
          name: "Name",
          label: "Name",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Skills",
          label: "Skills",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Last Updated",
          label: "Last Updated",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Recent assigned class",
          label: "Recent assigned class",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Category",
          label: "Category",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Items",
          label: "Items",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Created By",
          label: "Created By",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Share To",
          label: "Share To",
          layout: [
            {
              width: "30%",
              justifyContent: "center",
              alignItems: "center",
              items: [
                {
                  width: "100%",
                  height: "12px",
                },
              ],
            },
            {
              width: "70%",
              flexDirection: "row",
              items: [
                {
                  type: "rectangle",
                  width: "80%",
                  height: "12px",
                },
                {
                  width: "20%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
      ],
    };
  },
  computed: {
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    organizationData() {
      return this.$store.state.organization.currentOrganization;
    },
    studySetsCount() {
      return pathOr(0, ["paginateStudySets", "count"])(this);
    },
    studySets() {
      return pathOr([], ["paginateStudySets", "results"])(this);
    },
    pageCounter: always(PAGE_COUNTER),
    numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
    ...mapGetters("organization", [
      "isOrganizationAdmin",
      "isOrganizationAssociateAdmin",
      "currentOrganizationType",
      "isOrganizationTeacher",
      "isGeneralAdmin",
      "hasFilter",
      "isOrganizationK12",
    ]),
    shareableFilterOptions() {
      return [
        {
          text: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `study_sets.group`)),
          value: 'group',
        },
        {
          text: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `study_sets.organization`)),
          value: 'organization',
        },
        {
          text: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `study_sets.public`)),
          value: 'public',
        },
      ]
    },
    createdByFilterOptions() {
      return [
        {
          text: `Me (${this.$store.state.user.userInfo.email})`,
          value: 'me',
        },
        {
          text: this.$t(this.$i18nLabelsHelper(this.currentOrganizationType, `study_sets.organization`)),
          value: 'organization',
        },
        {
          text: this.$t('study_sets.table.other'),
          value: 'other',
        },
      ]
    },
    showTable() {
      return (this.studySets && this.studySets.length !== 0) || this.queryString != null || this.isFilter
    },
    filters: {
      get() {
        return this.$store.state.studysets.filters;
      },
      set(value) {
        this.setFiltersAction(value);
      },
    },
    isFilter: {
      get() {
        return this.$store.state.studysets.isFilter;
      },
      set(value) {
        this.setIsFilterAction(value);
      },
    },
  },
  methods: {
    ...mapActions({
      deleteStudySetAction: "studysets/deleteStudySet",
      checkStudySetPermissionAction: "studysets/checkStudySetPermission",
      setFiltersAction: "studysets/setFilters",
      setIsFilterAction: "studysets/setIsFilter",
    }),
    isStudySetEditable(studySet) {
      return !(studySet.assigned_group && studySet.assigned_group.length > 0);
    },
    getRecentAssignGroupName(studySet) {
      if (studySet.assigned_group && studySet.assigned_group.length > 0) {
        let x = studySet.assigned_group.reduce((prev, current) =>
          new Date(prev.created_at) > new Date(current.created_at) ? prev : current
        );
        return x.group_name;
      }
      return this.$t("common.none");
    },
    getCategoryLabel(v) {
      return getOptionsLabels(DefaultCategoryOptions, v);
    },
    handleDeleteStudySet(studySetId) {
      this.modals.showConfirmDelete = true;
      this.deleteStudySetIds = [studySetId];
    },
    async handleConfirmDeleteStudySet() {
      try {
        for (let idx = 0; idx < this.deleteStudySetIds.length; idx++) {
          await this.deleteStudySetAction(this.deleteStudySetIds[idx]);
        }
        successMessage(this.$t("study_sets.delete_study_set_success"))(this);
      } catch (error) {
        return warningMessage(this.$t("study_sets.delete_study_set_warning"))(this);
      } finally {
        this.deleteStudySetIds = [];
        await this.getStudySetPage(this.currentPage);
      }
    },
    getShareableLabel(v) {
      return getOptionsLabels(DefaultShareableOptions, v);
    },
    searchStudySet(search) {
      return this.getStudySet({ search });
    },
    getStudySetPage(page) {
      const search = this.queryString;
      return this.getStudySet({ page, search });
    },
    async getStudySet({ page = 1, search = null, status = "" }) {
      try {
        this.loading = true;
        let ordering = this.ordering;
        let filters = this.filters;
        let params = { page, search, status, ordering, ...filters };
        const { data } = await this.$axios.get(`/v1/study-sets/${this.organizationId}/`, { params });
        this.paginateStudySets = data;
        if (this.$refs.multipleTable) {
          this.$refs.multipleTable.clearSelection();
        }
        this.currentPage = page;
        this.selectedStudySets = [];
        this.actionModal = "";
        this.handleCheckStudySetPermission(data);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      return date ? format(new Date(date), this.dateFormat) : "--";
    },
    handleEditStudySet(studySetId) {
      if (this.canAccessStudySet(studySetId, "view")) {
        return warningMessage(this.$t("study_sets.edit_disabled_message"), "elsa-message info")(this);
      }

      return this.$router.push({
        name: "studyset-edit",
        params: {
          studySetId: studySetId,
        },
      });
    },
    async handleDuplicateStudySet(studySetId) {
      return this.$router.push({
        name: "studyset-duplicate",
        params: {
          studySetId: studySetId,
        },
      });
    },
    handleAssignToClassStudySet(studySet) {
      this.$refs.addAssignment.handleAssignToClassStudySet(studySet);
    },
    async handleReload() {
      await this.getStudySetPage(this.currentPage);
    },
    handleSelectRow(rowData) {
      this.selectedStudySets = rowData;
      if (this.selectedStudySets.length === this.paginateStudySets.results.length) {
        this.selectText = this.$t("common.deselect_all");
      } else {
        this.selectText = this.$t("common.select_all");
      }
    },
    changeActions(actions) {
      if (actions === "assign") {
        this.$refs.addAssignment.handleAssignToClassStudySets(this.selectedStudySets);
      } else if (actions === "remove") {
        this.modals.showConfirmDelete = true;

        this.deleteStudySetIds = this.selectedStudySets.map(x => x.id);
      }
    },
    selectAll(rows) {
      if (this.selectedStudySets.length < this.paginateStudySets.results.length) {
        this.$refs.multipleTable.clearSelection();
        rows.forEach(row => {
          this.$refs.multipleTable.toggleRowSelection(row);
        });
        this.selectedStudySets = rows;
        this.selectText = this.$t("common.deselect_all");
      } else {
        this.selectText = this.$t("common.select_all");
        this.selectedStudySets = [];
        this.$refs.multipleTable.clearSelection();
      }
    },
    async sortChange({ prop, order }) {
      const search = this.queryString;
      if (order === "descending") this.ordering = "-" + prop;
      else this.ordering = prop;
      await this.getStudySet({ search });
    },
    async handleCheckStudySetPermission(data) {
      try {
        const studySetIds = data.results.map(ss => ss.id);
        this.checkedStudySets = await this.checkStudySetPermissionAction(studySetIds);
      } catch (error) {
        error;
      }
    },
    canAccessStudySet(studySetId, action = "edit") {
      return this.checkedStudySets[action].includes(Number(studySetId));
    },
    getSkillsSupport(studySet) {
      if (studySet.items.length === 0) {
        return [STUDY_SET_SKILLS.SPEAKING];
      }
      let skills_support = [];
      for (let i = 0; i < studySet.items.length; i++) {
        skills_support = skills_support.concat(studySet.items[i].skills_support);
        skills_support = [...new Set(skills_support)];
        if (skills_support.length === 4) {
          return skills_support;
        }
      }
      return skills_support;
    },
    handleFilterChange(filters) {
      this.isFilter = true;
      this.filters = { ...this.filters, ...filters };
      this.getStudySet({});
    },
    isColumnFiltered(columnName) {
      return this.filters[columnName] && this.filters[columnName].length > 0 ? 'filtered-header' : '';
    },
  },
  async mounted() {
    try {
      DefaultCategoryOptions.forEach(option => {
        this.categoryFilterOptions.push({
          text: option.label,
          value: option.value,
        });
      });
      const params = { status: "" };
      await this.getStudySet(params);
    } catch (error) {
      error
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/_colors.scss";

.study-set-container {
  width: 100%;
  font-family: "SF Pro Display", sans-serif;

  .study-set-tab-body {
    text-align: center;
    padding: 24px 40px 0;

    .el-input {
      border: 1px solid #d8d8d8;
      border-radius: 8px;
    }
  }

  .create-new-study-set {
    button {
      background: #007eff;
      border-radius: 8px;
      border: none;
      color: #fff;
      padding: 16px;
      font-size: 16px;
      max-width: 230px;
      cursor: pointer;
      font-weight: 600;
      font-family: "SF Pro Display", sans-serif;
    }
  }

  .assign-to-group {
    button {
      background: #007eff;
      border-radius: 8px;
      border: none;
      color: #fff;
      padding: 8px 16px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      cursor: pointer;
      font-family: "SF Pro Display", sans-serif;
    }
  }

  .el-table .el-table__row .wrap-avatar small {
    font-size: 16px;
  }

  .elsa-table-layout-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    & > div {
      align-self: center;
    }
  }

  .status__label {
    font-size: 16px;
    text-transform: capitalize;
    padding-left: 10px;
  }

  .status__input {
    vertical-align: middle;

    .status__inner {
      border: 1px solid #dcdfe6;
      border-radius: 100%;
      width: 14px;
      height: 14px;
      background-color: #fff;
      cursor: pointer;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      position: relative;
      display: inline-block;
    }
  }

  .status__active .status__inner {
    border-color: #03bd23;
    background-color: #03bd23;
  }

  .status__finished .status__inner {
    border-color: #e6a23c;
    background-color: #e6a23c;
  }

  .status__not_started .status__inner {
    border-color: #909399;
    background-color: #909399;
  }

  .status__pending {
    display: block;
  }

  .status__pending .status__inner {
    border-color: #d9ecff;
    background-color: #d9ecff;
  }

  .wrap-avatar {
    .block {
      word-break: break-word;
    }
  }

  .el-menu.sidemenu {
    .el-menu-item {
      border: none;
      margin-top: 8px;
      margin-bottom: 24px;
      height: 48px !important;

      span {
        text-transform: capitalize;
      }

      svg {
        width: 30px;
      }
    }
  }

  .el-form {
    width: auto;
    padding-bottom: 0 !important;

    &.search-form {
      margin-top: 0;
      width: 316px;
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }

  .assign-to-class {
    margin-right: 8px;
    z-index: 1903;

    button {
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;
      padding: 8px 8px;
      border: 1px solid #007eff;
    }
  }

  .select-all {
    margin-top: 26px;
    margin-left: 16px;
    position: absolute;
  }

  .icon-wrap {
    display: flex;
    flex-wrap: wrap;

    .icon {
      margin: 0 0 0 0;
    }

    i {
      width: 24px;
      height: 24px;
    }
  }
}

.remove-study-set {
  color: red !important;
}

::v-deep .el-popper {
  margin-left: -75px !important;
  margin-top: 40px !important;
}

::v-deep .el-table__body,
::v-deep .el-table__footer,
::v-deep .el-table__header {
  width: 100% !important;
}

::v-deep .elsa-table-layout {
  padding-top: 0;
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 16px;
}

::v-deep .el-table .el-table-column--selection .cell {
  padding-left: 8px;
}

::v-deep .el-table .el-table__header-wrapper .cell {
  font-weight: 600;
  opacity: 1;
  color: #757575;
  padding: 12px 8px;
}

::v-deep .el-table .cell {
  padding: 12px 8px;
}

::v-deep .el-table__header tr {
  background-color: #f9f9f9 !important;
}

::v-deep .search-box.elsa-input {
  margin-top: 16px;
}

::v-deep .el-checkbox__inner {
  border-color: #828292 !important;
  border-radius: 4px !important;
}

.header-end {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-start;

  & > div {
    margin: 0 5px;
  }
}

.more-action-text {
  padding: 24px 0;
  font-weight: 600;
  margin-right: 8px;
  font-size: 16px;

  a {
    font-size: 16px;
    cursor: pointer;
  }
}

#bulkActionForm ::v-deep {
  input {
    box-sizing: border-box;
    outline: 2px transparent solid;
    border: 2.5px solid transparent;
  }

  .el-input__prefix,
  .el-input__suffix {
    top: 1px;
  }
}

::v-deep .study-set-tab-body {
  .el-input {
    height: 48px !important;

    .el-input__inner {
      padding: 24px 0 !important;
      opacity: 1 !important;
      font-size: 16px !important;
    }
  }
}

::v-deep .el-input-group__append {
  border-radius: 40px !important;
}

::v-deep .el-table__empty-block {
  width: 100% !important;
}

.remove-btn-div {
  display: flex;
  justify-content: flex-end;
}

.remove-btn {
  position: absolute;
  margin-top: 32px;
  margin-right: 48px;
  z-index: 1900;
  color: red;
  padding: 12px 16px;
}

::v-deep .el-table-column--selection .cell {
  width: 3000px;
}

::v-deep .el-table-column--selection .el-checkbox {
  width: 3000px;
  z-index: 1900;
  padding: 24px 8px;
}

::v-deep .el-table .cell {
  padding: 0 8px;
}

::v-deep .el-table__row {
  td:nth-child(2) {
    z-index: 1901;
  }
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 20px;
  color: $black;
}

::v-deep .el-table__column-filter-trigger .el-icon-arrow-down:before {
  content: "\e790";
}

.btn-edit-disabled {
  color: #c0c4cc !important;
  background-image: none;
  background-color: #fff;
  border-color: #ebeef5;

  svg {
    color: #c0c4cc !important;
  }
}

::v-deep .el-table .filtered-header .el-table__column-filter-trigger i {
  color: #409EFF;
}
#leave-modal-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 32px;
  text-align: center;
  color: $black;
  margin: 0 auto;
}
</style>
